'use client';

import { ResponsiveContainer, AreaChart as RAreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { ChartDataPoint } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  chartData: ChartDataPoint[];
  translations: ToolsTranslations['compareFundReturn'];
  locale: string;
}

export default function CompareChart({ chartData, translations, locale }: Props) {
  const yAxisLabel = `${translations.chart.yAxisLabel} (${translations.currencySuffix})`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = (props: any) => {
    const { payload, active } = props;
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload as ChartDataPoint | undefined;
    if (!data) return null;

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[220px]">
        <div className="mb-3 pb-2 border-b border-gray-100">
          <div className="text-sm font-semibold text-gray-900">{data.year}</div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center gap-4">
            <span className="text-sm text-gray-600">{translations.robotA}</span>
            <span className="text-sm font-semibold text-blue-700">
              {data.robotA.toLocaleString(locale)} {translations.currencySuffix}
            </span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <span className="text-sm text-gray-600">{translations.robotB}</span>
            <span className="text-sm font-semibold text-amber-700">
              {data.robotB.toLocaleString(locale)} {translations.currencySuffix}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">{translations.chart.title}</h3>
        <p className="text-xs text-gray-600">{translations.chart.subtitle}</p>
      </div>

      <div className="relative w-full bg-gray-50/40 rounded-xl pt-5 pb-4">
        <span className="absolute left-3 -top-1 text-xs text-gray-500">{yAxisLabel}</span>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RAreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" interval="preserveStartEnd" tickMargin={8} />
              <YAxis hide />
              <Tooltip content={CustomTooltip} />
              <Legend />
              <Area
                type="monotone"
                dataKey="robotA"
                name={translations.robotA}
                stroke="#2563eb"
                fill="#2563eb22"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="robotB"
                name={translations.robotB}
                stroke="#d97706"
                fill="#d9770622"
                strokeWidth={2}
              />
            </RAreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
